import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import "./mobile.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import TelegramButton from "./components/TelegramButton";
import TopSocialBar from "./components/TopSocialBar";
import PageMetadata from "./components/PageMetadata";

// Pages
import HomePage from "./pages/HomePage";
import NCERTMasteryPage from "./pages/NCERTMasteryPage";
import MentorshipPage from "./pages/MentorshipPage";
import AdhvaVibesPSIPage from "./pages/AdhvaVibesPSIPage";
import AdhvaPrelimsPath2026Page from "./pages/AdhvaPrelimsPath2026Page";
import AdhvaDiksoochiKAS2026Page from "./pages/AdhvaDiksoochiKAS2026Page";
import AdhvaMonthlyCurrentAffairsPage from "./pages/AdhvaMonthlyCurrentAffairsPage";
import ResourcesPage from "./pages/ResourcesPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import CurrentAffairsPage from "./pages/CurrentAffairsPage";
import CurrentAffairsTestPage from "./pages/CurrentAffairsTestPage";
import VideosPage from "./pages/VideosPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import NotFoundPage from "./pages/NotFoundPage";

// Auto-detect environment and set backend URL
const getBackendUrl = () => {
  // Detect environment and set appropriate backend URL
  const hostname = window.location.hostname;
  
  // Local development - use CORS proxy
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }
  
  // Production - use Render.com backend directly
  // CORS should be handled by the backend middleware
  return 'https://adhvaacaddemy-in-1.onrender.com';
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Log for debugging - ALWAYS log in browser console for troubleshooting
console.log('🔗 Backend URL:', BACKEND_URL);
console.log('🌍 Environment:', process.env.NODE_ENV);
console.log('🌐 Hostname:', window.location.hostname);

// Auth Context
export const AuthContext = createContext();
export const ThemeContext = createContext();

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

const MetadataPage = ({ title, description, noIndex = false, children }) => (
  <>
    <PageMetadata title={title} description={description} noIndex={noIndex} />
    {children}
  </>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const checkAuth = async () => {
    try {
      // Get session token from localStorage
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      // Clear invalid session token
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { user, session_token } = response.data;

      // Store session token in localStorage
      localStorage.setItem('session_token', session_token);

      setUser(user);
      toast.success("Logged in successfully!");
      return true;
    } catch (error) {
      // Detailed error message for debugging
      let errorMessage = "Login failed";
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.detail || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request made but no response received
        errorMessage = `Cannot connect to backend server at ${BACKEND_URL}. Please check if the backend is running.`;
      } else {
        // Something else happened
        errorMessage = error.message || "Unknown error occurred";
      }
      
      console.error('Login error:', {
        message: error.message,
        response: error.response,
        request: error.request,
        backendUrl: BACKEND_URL
      });
      
      toast.error(errorMessage);
      return false;
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      setUser(response.data.user);
      toast.success("Account created successfully!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
      // Clear session token from localStorage
      localStorage.removeItem('session_token');
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      // Clear session anyway
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" style={{ borderColor: 'var(--accent)' }}></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, checkAuth }}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <BrowserRouter>
          <TopSocialBar />
          <Routes>
            <Route path="/" element={<MetadataPage title="UPSC, KPSC & PSI Preparation | Adhva Academy" description="Prepare for UPSC, KPSC KAS, PSI and other competitive exams with Adhva Academy mentorship, test series and structured learning paths."><HomePage /></MetadataPage>} />
            <Route path="/ncert-mastery" element={<MetadataPage title="NCERT Foundation Test Series | Adhva Academy" description="Build a strong UPSC and KPSC foundation with Adhva Academy's NCERT test series, expert analysis and guided practice."><NCERTMasteryPage /></MetadataPage>} />
            <Route path="/mentorship" element={<MetadataPage title="UPSC Mentorship Program | Adhva Academy" description="Get personalised UPSC preparation guidance, accountability and strategy support through Adhva Academy's mentorship program."><MentorshipPage /></MetadataPage>} />
            <Route path="/adhva-vibes-psi" element={<MetadataPage title="Karnataka PSI Test Series | Adhva Academy" description="Prepare for the Karnataka PSI exam with Adhva Vibes: structured tests, feedback and a focused success roadmap."><AdhvaVibesPSIPage /></MetadataPage>} />
            <Route path="/adhva-prelims-path-2026" element={<MetadataPage title="Adhva Prelims Path 2026 | UPSC Test Series" description="Prepare for UPSC Prelims 2026 with complete test coverage, mentoring, revision support and performance analysis from Adhva Academy."><AdhvaPrelimsPath2026Page /></MetadataPage>} />
            <Route path="/adhva-diksoochi-kas-2026" element={<MetadataPage title="Adhva Diksoochi KAS 2026 | KPSC KAS Prelims" description="Prepare for KPSC KAS Prelims 2026 with 30 structured tests, one-to-one mentoring and detailed performance insights."><AdhvaDiksoochiKAS2026Page /></MetadataPage>} />
            <Route path="/adhva-monthly-current-affairs" element={<MetadataPage title="Monthly Current Affairs Test Series | Adhva Academy" description="Stay exam-ready with Adhva Academy's monthly current affairs test series for UPSC and other competitive exams."><AdhvaMonthlyCurrentAffairsPage /></MetadataPage>} />
            <Route path="/resources/*" element={<MetadataPage title="UPSC & KPSC Study Resources | Adhva Academy" description="Explore Adhva Academy study resources including NCERT notes, current affairs, PYQs, MCQs and preparation strategies."><ResourcesPage /></MetadataPage>} />
            <Route path="/blog" element={<MetadataPage title="Super Insight Blog | Adhva Academy" description="Read UPSC, KPSC and competitive-exam preparation insights, strategies and updates from Adhva Academy."><BlogPage /></MetadataPage>} />
            <Route path="/blog/:slug" element={<MetadataPage title="Article | Adhva Academy" description="Read preparation insights and exam updates from Adhva Academy."><BlogDetailPage /></MetadataPage>} />
            <Route path="/current-affairs" element={<MetadataPage title="Current Affairs | Adhva Academy" description="Follow current affairs updates and preparation support for UPSC, KPSC KAS and competitive examinations."><CurrentAffairsPage /></MetadataPage>} />
            <Route path="/current-affairs-test" element={<MetadataPage title="Current Affairs Monthly Test Series | Adhva Academy" description="Practice current affairs with Adhva Academy's monthly test series for UPSC, State PSC and competitive exams."><CurrentAffairsTestPage /></MetadataPage>} />
            <Route path="/videos" element={<MetadataPage title="Video Library | Adhva Academy" description="Watch Adhva Academy video lectures, exam strategies and guidance for UPSC, KPSC and competitive-exam preparation."><VideosPage /></MetadataPage>} />
            <Route path="/about-us" element={<MetadataPage title="About Adhva Academy | The Learning Path" description="Learn about Adhva Academy and our mission to guide UPSC, KPSC, PSI and competitive-exam aspirants to success."><AboutPage /></MetadataPage>} />
            <Route path="/contact-us" element={<MetadataPage title="Contact Adhva Academy | Mysuru" description="Contact Adhva Academy in Kuvempunagar, Mysuru for UPSC, KPSC KAS, PSI courses, mentorship and admissions support."><ContactPage /></MetadataPage>} />
            <Route path="/auth" element={<MetadataPage title="Admin Login | Adhva Academy" description="Secure Adhva Academy administration login." noIndex><AuthPage /></MetadataPage>} />
            <Route path="/forgot-password" element={<MetadataPage title="Reset Admin Password | Adhva Academy" description="Reset your Adhva Academy administration password." noIndex><ForgotPasswordPage /></MetadataPage>} />
            <Route
              path="/admin/dashboard"
              element={user ? <MetadataPage title="Admin Dashboard | Adhva Academy" description="Adhva Academy administration dashboard." noIndex><AdminDashboard /></MetadataPage> : <Navigate to="/auth" />}
            />
            <Route path="*" element={<MetadataPage title="Page Not Found | Adhva Academy" description="The page you requested could not be found." noIndex><NotFoundPage /></MetadataPage>} />
          </Routes>
          <Toaster position="top-right" />
          <TelegramButton />
        </BrowserRouter>
      </ThemeContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
console.log('REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
